import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { QuestionSet, Question, OptionKey, DifficultyLevel } from '../../types';
import {
  Plus,
  Layers,
  Edit2,
  Trash2,
  Clock,
  Shuffle,
  HelpCircle,
  X,
  ArrowRight,
  BookOpen,
  RotateCcw,
  Copy,
  Download,
  Upload,
  FileText,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';
import { useToast } from '../ui/Toast';
import { ConfirmModal } from '../common/ConfirmModal';

export const QuestionSetManager: React.FC = () => {
  const {
    sectors,
    subjects,
    questionSets,
    questions,
    addQuestionSet,
    updateQuestionSet,
    deleteQuestionSet,
    clearQuestionsInSet,
    setCurrentView,
    importQuestions,
  } = useApp();
  const { showToast } = useToast();

  const [deleteSetTarget, setDeleteSetTarget] = useState<QuestionSet | null>(null);
  const [clearSetTarget, setClearSetTarget] = useState<QuestionSet | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Guided CSV Import Modal states
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importStep, setImportStep] = useState<1 | 2 | 3>(1);
  const [importTargetSetId, setImportTargetSetId] = useState<number>(
    questionSets[0]?.id || 1
  );
  const [importCsvText, setImportCsvText] = useState('');
  const [replaceExistingOnImport, setReplaceExistingOnImport] = useState(false);
  const [detectedSet, setDetectedSet] = useState<QuestionSet | null>(null);
  const [isReplaceConfirmModalOpen, setIsReplaceConfirmModalOpen] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string>('');
  const [parsedRows, setParsedRows] = useState<
    {
      valid: boolean;
      isDuplicate?: boolean;
      duplicateReason?: string;
      data: Omit<Question, 'id'> | null;
      rawText: string;
      error?: string;
    }[]
  >([]);

  // CSV Export (Exports questions in the 15-column bilingual schema)
  const handleExportCSV = () => {
    const headers = [
      'question_set_id',
      'question_text_en',
      'question_text_hi',
      'option_a_en',
      'option_a_hi',
      'option_b_en',
      'option_b_hi',
      'option_c_en',
      'option_c_hi',
      'option_d_en',
      'option_d_hi',
      'correct_option',
      'difficulty',
      'explanation_en',
      'explanation_hi',
    ];
    const rows = questions.map((q) => [
      q.question_set_id,
      `"${q.question_text.replace(/"/g, '""')}"`,
      `"${(q.question_text_hi || '').replace(/"/g, '""')}"`,
      `"${q.option_a.replace(/"/g, '""')}"`,
      `"${(q.option_a_hi || '').replace(/"/g, '""')}"`,
      `"${q.option_b.replace(/"/g, '""')}"`,
      `"${(q.option_b_hi || '').replace(/"/g, '""')}"`,
      `"${q.option_c.replace(/"/g, '""')}"`,
      `"${(q.option_c_hi || '').replace(/"/g, '""')}"`,
      `"${q.option_d.replace(/"/g, '""')}"`,
      `"${(q.option_d_hi || '').replace(/"/g, '""')}"`,
      q.correct_option,
      q.difficulty,
      `"${(q.explanation || '').replace(/"/g, '""')}"`,
      `"${(q.explanation_hi || '').replace(/"/g, '""')}"`,
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `mcq_questions_bilingual_export_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast(`Exported ${questions.length} questions to CSV`, 'success');
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      showToast('Please select a valid .csv file', 'error');
      e.target.value = '';
      return;
    }

    setUploadedFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result;
      if (typeof content === 'string') {
        setImportCsvText(content);
        showToast(`Loaded "${file.name}" (${(file.size / 1024).toFixed(1)} KB)`, 'info');
      }
    };
    reader.onerror = () => {
      showToast('Failed to read the selected file. Please try again.', 'error');
    };
    reader.readAsText(file);
  };

  // Download CSV Template (15-column primary schema)
  const handleDownloadTemplate = () => {
    const headers = [
      'question_set_id',
      'question_text_en',
      'question_text_hi',
      'option_a_en',
      'option_a_hi',
      'option_b_en',
      'option_b_hi',
      'option_c_en',
      'option_c_hi',
      'option_d_en',
      'option_d_hi',
      'correct_option',
      'difficulty',
      'explanation_en',
      'explanation_hi',
    ];
    const exampleRow = [
      importTargetSetId,
      '"Which organ pumps blood throughout the human body?"',
      '"मानव शरीर में कौन सा अंग रक्त पंप करता है?"',
      '"Lungs"',
      '"फेफड़े"',
      '"Heart"',
      '"हृदय"',
      '"Brain"',
      '"मस्तिष्क"',
      '"Liver"',
      '"यकृत"',
      'B',
      'Easy',
      '"The heart is the primary circulatory pump in humans."',
      '"हृदय मानव में प्राथमिक परिसंचरण पंप है।"',
    ];

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), exampleRow.join(',')].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `bilingual_question_import_template_set_${importTargetSetId}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast('Downloaded official 15-column Bilingual CSV Template', 'success');
  };

  // Robust RFC 4180 CSV State-Machine Parser
  const parseRFC4180CSV = (text: string): { rows: string[][]; error?: string } => {
    // Strip optional UTF-8 BOM
    const clean = text.replace(/^\uFEFF/, '');
    const rows: string[][] = [];
    let currentRow: string[] = [];
    let currentField = '';
    let inQuotes = false;
    let fieldHadQuotes = false;
    let quoteClosed = false;
    let lineNum = 1;
    let i = 0;
    const len = clean.length;

    while (i < len) {
      const char = clean[i];

      if (inQuotes) {
        if (char === '"') {
          if (i + 1 < len && clean[i + 1] === '"') {
            // Escaped double quote ("") inside quoted field
            currentField += '"';
            i += 2;
          } else {
            // Closing quote
            inQuotes = false;
            quoteClosed = true;
            i++;
          }
        } else {
          if (char === '\n') lineNum++;
          currentField += char;
          i++;
        }
      } else {
        if (quoteClosed) {
          // After closing quote, only delimiters, whitespace, or newlines are valid
          if (char === ',') {
            currentRow.push(currentField);
            currentField = '';
            fieldHadQuotes = false;
            quoteClosed = false;
            i++;
          } else if (char === '\r') {
            if (i + 1 < len && clean[i + 1] === '\n') i++;
            currentRow.push(currentField);
            currentField = '';
            fieldHadQuotes = false;
            quoteClosed = false;
            lineNum++;
            rows.push(currentRow);
            currentRow = [];
            i++;
          } else if (char === '\n') {
            currentRow.push(currentField);
            currentField = '';
            fieldHadQuotes = false;
            quoteClosed = false;
            lineNum++;
            rows.push(currentRow);
            currentRow = [];
            i++;
          } else if (char === ' ' || char === '\t') {
            // Trailing spaces after quote before comma/newline can be ignored
            i++;
          } else {
            return {
              rows: [],
              error: `Malformed CSV around line ${lineNum}: Unexpected character '${char}' immediately following a closing quote. Quoted fields must be followed by a comma or newline.`,
            };
          }
        } else if (char === '"') {
          if (currentField.length > 0 && currentField.trim().length > 0) {
            return {
              rows: [],
              error: `Malformed CSV around line ${lineNum}: Unescaped quote character found inside an unquoted field. Quotes within values must be escaped as double quotes ("").`,
            };
          }
          inQuotes = true;
          fieldHadQuotes = true;
          i++;
        } else if (char === ',') {
          currentRow.push(fieldHadQuotes ? currentField : currentField.trim());
          currentField = '';
          fieldHadQuotes = false;
          quoteClosed = false;
          i++;
        } else if (char === '\r') {
          if (i + 1 < len && clean[i + 1] === '\n') i++;
          currentRow.push(fieldHadQuotes ? currentField : currentField.trim());
          currentField = '';
          fieldHadQuotes = false;
          quoteClosed = false;
          lineNum++;
          if (currentRow.length > 1 || (currentRow.length === 1 && currentRow[0] !== '')) {
            rows.push(currentRow);
          }
          currentRow = [];
          i++;
        } else if (char === '\n') {
          currentRow.push(fieldHadQuotes ? currentField : currentField.trim());
          currentField = '';
          fieldHadQuotes = false;
          quoteClosed = false;
          lineNum++;
          if (currentRow.length > 1 || (currentRow.length === 1 && currentRow[0] !== '')) {
            rows.push(currentRow);
          }
          currentRow = [];
          i++;
        } else {
          currentField += char;
          i++;
        }
      }
    }

    if (inQuotes) {
      return {
        rows: [],
        error: `Malformed CSV: Unclosed quoted field detected starting near line ${lineNum}. Please check that all opened quotes are properly closed.`,
      };
    }

    if (currentField.length > 0 || currentRow.length > 0 || fieldHadQuotes || quoteClosed) {
      currentRow.push(fieldHadQuotes ? currentField : currentField.trim());
      if (currentRow.length > 1 || (currentRow.length === 1 && currentRow[0] !== '')) {
        rows.push(currentRow);
      }
    }

    return { rows };
  };

  // CSV Validation & Step Progress (Supports both 15-column bilingual and 9-column legacy)
  const handleParseAndValidateCSV = () => {
    try {
      if (!importCsvText || !importCsvText.trim()) {
        showToast('Please provide a header line and at least one CSV question row', 'error');
        return;
      }

      // 1. Resolve and validate the selected Question Set from the dropdown
      const selectedTargetSet = questionSets.find((qs) => qs.id === importTargetSetId);
      if (!selectedTargetSet) {
        showToast(
          `Invalid destination: The selected Question Set (ID #${importTargetSetId}) does not exist in the database. Please select a valid Question Set.`,
          'error',
          'Invalid Destination'
        );
        return;
      }

      // 2. Linear-time RFC 4180 parsing with malformed CSV checks
      const parseResult = parseRFC4180CSV(importCsvText);
      if (parseResult.error) {
        showToast(parseResult.error, 'error', 'CSV Format Error');
        return;
      }

      const rows = parseResult.rows;
      if (rows.length <= 1) {
        showToast('Please provide a header line and at least one CSV question row', 'error');
        return;
      }

      // 3. Header validation
      const headerCols = rows[0].map((c) => c.replace(/^\uFEFF/, '').trim().toLowerCase());

      const header15Col = [
        'question_set_id',
        'question_text_en',
        'question_text_hi',
        'option_a_en',
        'option_a_hi',
        'option_b_en',
        'option_b_hi',
        'option_c_en',
        'option_c_hi',
        'option_d_en',
        'option_d_hi',
        'correct_option',
        'difficulty',
        'explanation_en',
        'explanation_hi',
      ];

      const header9Col = [
        'question_set_id',
        'question_text',
        'option_a',
        'option_b',
        'option_c',
        'option_d',
        'correct_option',
        'difficulty',
        'explanation',
      ];

      // Detect index / id column in first position specifically
      const firstColHeader = headerCols[0] || '';
      if (firstColHeader === 'index' || firstColHeader === 'id' || firstColHeader === '#') {
        showToast(
          `Invalid CSV header: Column 1 is "${headerCols[0]}", but the importer expects "question_set_id". Please download and use the official CSV Template.`,
          'error'
        );
        return;
      }

      const is15Col = headerCols.length === 15;
      const is9Col = headerCols.length === 9;

      if (!is15Col && !is9Col) {
        showToast(
          `Invalid CSV header schema: Expected either 15 columns (Bilingual) or 9 columns (Legacy), but received ${headerCols.length} columns. Please download and use the official template.`,
          'error'
        );
        return;
      }

      const targetHeader = is15Col ? header15Col : header9Col;
      for (let c = 0; c < targetHeader.length; c++) {
        if (headerCols[c] !== targetHeader[c]) {
          showToast(
            `Invalid CSV header schema: Column ${c + 1} is "${headerCols[c]}", but expected "${targetHeader[c]}". Expected schema: ${targetHeader.join(',')}`,
            'error'
          );
          return;
        }
      }

      const parsed: typeof parsedRows = [];
      const setId = selectedTargetSet.id;

      // 4. Parse question data rows
      for (let i = 1; i < rows.length; i++) {
        const cols = rows[i];
        const rowRawPreview = cols.map((col) => (col.includes(',') || col.includes('\n') ? `"${col.replace(/"/g, '""')}"` : col)).join(',');

        if (is15Col && cols.length >= 15) {
          const qTextEn = cols[1];
          const qTextHi = cols[2] || '';
          const optAEn = cols[3];
          const optAHi = cols[4] || '';
          const optBEn = cols[5];
          const optBHi = cols[6] || '';
          const optCEn = cols[7];
          const optCHi = cols[8] || '';
          const optDEn = cols[9];
          const optDHi = cols[10] || '';
          const correct = (cols[11].trim().toUpperCase() as OptionKey) || 'A';
          const diff = (cols[12].trim() as DifficultyLevel) || 'Medium';
          const explEn = cols[13] || '';
          const explHi = cols[14] || '';

          if (!qTextEn || !optAEn || !optBEn || !optCEn || !optDEn) {
            parsed.push({
              valid: false,
              data: null,
              rawText: rowRawPreview,
              error: 'Missing required English options or question statement',
            });
          } else {
            parsed.push({
              valid: true,
              data: {
                question_set_id: setId,
                question_text: qTextEn,
                question_text_hi: qTextHi,
                option_a: optAEn,
                option_a_hi: optAHi,
                option_b: optBEn,
                option_b_hi: optBHi,
                option_c: optCEn,
                option_c_hi: optCHi,
                option_d: optDEn,
                option_d_hi: optDHi,
                correct_option: ['A', 'B', 'C', 'D'].includes(correct) ? correct : 'A',
                difficulty: ['Easy', 'Medium', 'Hard'].includes(diff) ? diff : 'Medium',
                explanation: explEn,
                explanation_hi: explHi,
                status: 'active',
              },
              rawText: rowRawPreview,
            });
          }
        } else if (is9Col && cols.length >= 7) {
          const qText = cols[1];
          const optA = cols[2];
          const optB = cols[3];
          const optC = cols[4];
          const optD = cols[5];
          const correct = (cols[6].trim().toUpperCase() as OptionKey) || 'A';
          const diff = (cols[7].trim() as DifficultyLevel) || 'Medium';
          const expl = cols[8] || '';

          if (!qText || !optA || !optB || !optC || !optD) {
            parsed.push({
              valid: false,
              data: null,
              rawText: rowRawPreview,
              error: 'Missing required options or question statement',
            });
          } else {
            parsed.push({
              valid: true,
              data: {
                question_set_id: setId,
                question_text: qText,
                question_text_hi: '',
                option_a: optA,
                option_a_hi: '',
                option_b: optB,
                option_b_hi: '',
                option_c: optC,
                option_c_hi: '',
                option_d: optD,
                option_d_hi: '',
                correct_option: ['A', 'B', 'C', 'D'].includes(correct) ? correct : 'A',
                difficulty: ['Easy', 'Medium', 'Hard'].includes(diff) ? diff : 'Medium',
                explanation: expl,
                explanation_hi: '',
                status: 'active',
              },
              rawText: rowRawPreview,
            });
          }
        } else {
          parsed.push({
            valid: false,
            data: null,
            rawText: rowRawPreview,
            error: `Expected ${is15Col ? '15' : '9'} columns, received ${cols.length}`,
          });
        }
      }

      // Set detected target set from the selected dropdown set
      setDetectedSet(selectedTargetSet);

      // Normalization fingerprint helper: English question_text + English option_a..d
      const makeKey = (qText: string, a: string, b: string, c: string, d: string) => {
        const norm = (s: string) => (s || '').trim().toLowerCase().replace(/\s+/g, ' ');
        return `${norm(qText)}|||${norm(a)}|||${norm(b)}|||${norm(c)}|||${norm(d)}`;
      };

      // Build existing question fingerprints for each set ID from state
      const existingKeysBySet: Record<number, Set<string>> = {};
      questions.forEach((q) => {
        const sId = Number(q.question_set_id);
        if (!existingKeysBySet[sId]) existingKeysBySet[sId] = new Set();
        existingKeysBySet[sId].add(
          makeKey(q.question_text, q.option_a, q.option_b, q.option_c, q.option_d)
        );
      });

      // Track duplicate keys within uploaded CSV
      const csvSeenKeysBySet: Record<number, Set<string>> = {};

      parsed.forEach((row) => {
        if (row.valid && row.data) {
          const sId = Number(row.data.question_set_id);
          const fp = makeKey(
            row.data.question_text,
            row.data.option_a,
            row.data.option_b,
            row.data.option_c,
            row.data.option_d
          );

          if (!csvSeenKeysBySet[sId]) csvSeenKeysBySet[sId] = new Set();

          const existsInDb = existingKeysBySet[sId]?.has(fp);
          const existsInCsv = csvSeenKeysBySet[sId]?.has(fp);

          if (existsInDb) {
            row.isDuplicate = true;
            row.duplicateReason = `Duplicate of existing question in Question Set #${sId}`;
          } else if (existsInCsv) {
            row.isDuplicate = true;
            row.duplicateReason = `Duplicate row in uploaded CSV (Question Set #${sId})`;
          } else {
            csvSeenKeysBySet[sId].add(fp);
          }
        }
      });

      setParsedRows(parsed);
      setImportStep(2);
    } catch (err) {
      showToast('Error parsing CSV format: ' + String(err), 'error');
    }
  };

  const executeImport = async () => {
    const validQuestionsToImport = parsedRows
      .filter((r) => r.valid && r.data !== null && (replaceExistingOnImport || !r.isDuplicate))
      .map((r) => r.data as Omit<Question, 'id'>);

    const duplicateCount = parsedRows.filter((r) => r.valid && r.isDuplicate).length;

    if (validQuestionsToImport.length === 0) {
      if (duplicateCount > 0 && !replaceExistingOnImport) {
        showToast(
          `All ${duplicateCount} valid questions in CSV are duplicates of existing questions in target Set #${importTargetSetId}. Turn ON "Replace existing questions" if you wish to overwrite.`,
          'info',
          'No New Questions'
        );
      } else {
        showToast('No valid questions available to import', 'error');
      }
      return;
    }

    try {
      const count = await importQuestions(validQuestionsToImport, {
        replaceExisting: replaceExistingOnImport,
        targetSetId: importTargetSetId,
        targetSetCode: detectedSet?.code,
      });

      const targetSet = questionSets.find((qs) => qs.id === importTargetSetId);
      const setLabel = targetSet ? ` "${targetSet.name}"` : '';

      let msg = '';
      if (replaceExistingOnImport) {
        msg = `Replaced existing questions and imported ${count} questions into${setLabel}!`;
      } else if (duplicateCount > 0) {
        msg = `Imported ${count} new questions into${setLabel} (${duplicateCount} duplicate${duplicateCount > 1 ? 's' : ''} skipped).`;
      } else {
        msg = `Successfully imported ${count} questions into${setLabel}!`;
      }

      showToast(msg, 'success', 'Import Completed');
      setIsImportModalOpen(false);
      setImportCsvText('');
      setImportStep(1);
      setParsedRows([]);
      setReplaceExistingOnImport(false);
      setIsReplaceConfirmModalOpen(false);
      setUploadedFileName('');
      setDetectedSet(null);
    } catch (err: any) {
      showToast(`Import failed: ${err.message || String(err)}`, 'error', 'Import Error');
    }
  };

  const handleConfirmImport = async () => {
    // If replaceExistingOnImport is enabled and target set has existing questions, request explicit confirmation
    const targetExistingCount = questions.filter((q) => Number(q.question_set_id) === Number(importTargetSetId)).length;
    if (replaceExistingOnImport && targetExistingCount > 0) {
      setIsReplaceConfirmModalOpen(true);
      return;
    }

    await executeImport();
  };

  const confirmDeleteSet = () => {
    if (!deleteSetTarget) return;
    deleteQuestionSet(deleteSetTarget.id);
    showToast('Question set deleted', 'info');
    setDeleteSetTarget(null);
  };

  const confirmClearSet = async () => {
    if (!clearSetTarget) return;
    const count = questions.filter((q) => q.question_set_id === clearSetTarget.id).length;
    const ok = await clearQuestionsInSet(clearSetTarget.id);
    if (ok) {
      showToast(`Cleared all ${count} questions from "${clearSetTarget.name}"`, 'info');
    } else {
      showToast('Failed to clear questions', 'error');
    }
    setClearSetTarget(null);
  };
  const [editingSet, setEditingSet] = useState<QuestionSet | null>(null);

  const [formData, setFormData] = useState({
    sector_id: sectors[0]?.id || 1,
    subject_id: subjects[0]?.id || 1,
    name: '',
    description: '',
    instructions: '',
    status: 'published' as 'published' | 'draft',
    random_questions: false,
    random_options: false,
    time_limit_minutes: 10,
  });

  const handleOpenAdd = () => {
    setEditingSet(null);
    setFormData({
      sector_id: sectors[0]?.id || 1,
      subject_id: subjects[0]?.id || 1,
      name: '',
      description: '',
      instructions: '',
      status: 'published',
      random_questions: false,
      random_options: false,
      time_limit_minutes: 10,
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (qs: QuestionSet) => {
    setEditingSet(qs);
    setFormData({
      sector_id: qs.sector_id,
      subject_id: qs.subject_id,
      name: qs.name,
      description: qs.description,
      instructions: qs.instructions,
      status: qs.status,
      random_questions: qs.random_questions,
      random_options: qs.random_options,
      time_limit_minutes: qs.time_limit_minutes,
    });
    setIsModalOpen(true);
  };

  const handleOpenCopy = (qs: QuestionSet) => {
    setEditingSet(null);
    setFormData({
      sector_id: qs.sector_id,
      subject_id: qs.subject_id,
      name: qs.name,
      description: qs.description,
      instructions: qs.instructions,
      status: qs.status,
      random_questions: qs.random_questions,
      random_options: qs.random_options,
      time_limit_minutes: qs.time_limit_minutes,
    });
    setIsModalOpen(true);
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    if (editingSet) {
      updateQuestionSet(editingSet.id, formData);
      showToast('Question set updated', 'success');
    } else {
      addQuestionSet(formData);
      showToast('New question set published', 'success');
    }
    setIsModalOpen(false);
  };

  return (
    <div className="space-y-6 pb-24 md:pb-16">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-[#111111] tracking-tight">
            Question Sets & Exam Modules
          </h1>
          <p className="text-xs sm:text-sm text-[#5F6368]">
            Configure test packages, set durations, candidate instructions, and publishing status.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5 self-start sm:self-auto">
          <button
            onClick={handleExportCSV}
            className="px-3.5 py-2.5 rounded-xl border border-[#E5E9E9] hover:bg-[#F0F4F4] text-[#111111] text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer"
            title="Download questions as CSV"
          >
            <Download className="w-4 h-4 text-[#1b8e88]" />
            Export CSV
          </button>

          <button
            onClick={() => {
              setImportStep(1);
              setIsImportModalOpen(true);
            }}
            className="px-3.5 py-2.5 rounded-xl border border-[#E5E9E9] hover:bg-[#F0F4F4] text-[#111111] text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer"
            title="Bulk import questions from CSV"
          >
            <Upload className="w-4 h-4 text-[#1b8e88]" />
            Import CSV
          </button>

          <button
            onClick={handleOpenAdd}
            className="px-4 py-2.5 rounded-xl bg-[#56E8E1] hover:bg-[#34d5cd] text-[#111111] text-xs font-bold flex items-center gap-1.5 shadow-xs transition-all cursor-pointer"
          >
            <Plus className="w-4 h-4 text-[#111111]" />
            New Question Set
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {questionSets.map((qs) => {
          const parentSector = sectors.find((s) => s.id === qs.sector_id);
          const parentSubject = subjects.find((sub) => sub.id === qs.subject_id);
          const qCount = questions.filter((q) => q.question_set_id === qs.id).length;

          return (
            <div
              key={qs.id}
              className="bg-white rounded-2xl border border-[#E5E9E9] p-5 flex flex-col justify-between hover:border-[#56E8E1]/60 transition-all shadow-[0_2px_12px_rgba(17,17,17,0.02)]"
            >
              <div>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <span className="px-2.5 py-0.5 rounded-md bg-[#FFF7D9] text-[#8a6508] border border-[#EBC138]/40 text-[11px] font-semibold">
                    {parentSubject?.name || 'Subject'}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleOpenCopy(qs)}
                      className="p-1.5 rounded-lg text-[#5F6368] hover:text-[#1b8e88] hover:bg-[#E8FAF9] transition-colors cursor-pointer"
                      title="Copy Question Set"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleOpenEdit(qs)}
                      className="p-1.5 rounded-lg text-[#5F6368] hover:text-[#1b8e88] hover:bg-[#E8FAF9] transition-colors cursor-pointer"
                      title="Edit set"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setClearSetTarget(qs)}
                      disabled={qCount === 0}
                      className={`p-1.5 rounded-lg transition-colors cursor-pointer ${
                        qCount === 0
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'text-[#5F6368] hover:text-[#D97706] hover:bg-[#FEF3C7]'
                      }`}
                      title={
                        qCount === 0
                          ? 'No questions to clear'
                          : `Clear all ${qCount} questions in this set`
                      }
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setDeleteSetTarget(qs)}
                      className="p-1.5 rounded-lg text-[#5F6368] hover:text-[#D94A5C] hover:bg-[#FDEDEF] transition-colors cursor-pointer"
                      title="Delete set"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                <h3 className="text-base font-bold text-[#111111] mb-1">{qs.name}</h3>
                <p className="text-xs text-[#5F6368] line-clamp-2 mb-4 leading-relaxed">
                  {qs.description || 'No description provided.'}
                </p>

                <div className="grid grid-cols-2 gap-2 text-[11px] text-[#111111] bg-[#F7FAFA] p-3 rounded-xl mb-4 border border-[#E5E9E9]">
                  <div className="flex items-center gap-1.5">
                    <HelpCircle className="w-3.5 h-3.5 text-[#1b8e88]" />
                    <span>{qCount} Questions</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-[#1b8e88]" />
                    <span>{qs.time_limit_minutes} min test</span>
                  </div>
                  <div className="flex items-center gap-1.5 col-span-2">
                    <Shuffle className="w-3.5 h-3.5 text-[#8a6508]" />
                    <span className="text-[#5F6368]">
                      {qs.random_questions ? 'Shuffled questions' : 'Sequential order'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="pt-3 border-t border-[#E5E9E9] flex items-center justify-between">
                <span
                  className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                    qs.status === 'published'
                      ? 'bg-[#EAF8F1] text-[#20A66A] border border-[#20A66A]/30'
                      : 'bg-[#F0F4F4] text-[#5F6368] border border-[#E5E9E9]'
                  }`}
                >
                  {qs.status}
                </span>

                <button
                  onClick={() => setCurrentView('admin-questions')}
                  className="text-xs font-bold text-[#1b8e88] hover:text-[#146e69] flex items-center gap-1 cursor-pointer"
                >
                  View MCQs
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add / Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-[#111111]/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-7 shadow-2xl border border-[#E5E9E9]">
            <div className="flex items-center justify-between pb-3 border-b border-[#E5E9E9] mb-4">
              <h3 className="text-base font-bold text-[#111111]">
                {editingSet ? 'Edit Question Set' : 'New Question Set'}
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-[#5F6368] hover:text-[#111111] p-1 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-[#111111] mb-1">
                    Sector
                  </label>
                  <select
                    value={formData.sector_id}
                    onChange={(e) =>
                      setFormData({ ...formData, sector_id: Number(e.target.value) })
                    }
                    className="w-full px-3 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1] bg-white"
                  >
                    {sectors.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#111111] mb-1">
                    Subject
                  </label>
                  <select
                    value={formData.subject_id}
                    onChange={(e) =>
                      setFormData({ ...formData, subject_id: Number(e.target.value) })
                    }
                    className="w-full px-3 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1] bg-white"
                  >
                    {subjects.map((sub) => (
                      <option key={sub.id} value={sub.id}>
                        {sub.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-[#111111] mb-1">
                  Set Title *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Coding-Decoding — Set 2"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-[#111111] mb-1">
                  Description
                </label>
                <input
                  type="text"
                  placeholder="Brief summary of syllabus or topics covered..."
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="w-full px-3 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-[#111111] mb-1">
                  Candidate Exam Instructions
                </label>
                <textarea
                  rows={2}
                  placeholder="Instructions displayed to candidates prior to starting..."
                  value={formData.instructions}
                  onChange={(e) =>
                    setFormData({ ...formData, instructions: e.target.value })
                  }
                  className="w-full px-3 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1]"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-[#111111] mb-1">
                    Exam Duration (Minutes)
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={180}
                    value={formData.time_limit_minutes}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        time_limit_minutes: Number(e.target.value) || 10,
                      })
                    }
                    className="w-full px-3 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#111111] mb-1">
                    Publish Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        status: e.target.value as 'published' | 'draft',
                      })
                    }
                    className="w-full px-3 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1] bg-white"
                  >
                    <option value="published">Published (Visible to Students)</option>
                    <option value="draft">Draft (Hidden)</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2 pt-2 border-t border-[#E5E9E9]">
                <label className="flex items-center gap-2 text-xs font-semibold text-[#111111] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.random_questions}
                    onChange={(e) =>
                      setFormData({ ...formData, random_questions: e.target.checked })
                    }
                    className="rounded accent-[#56E8E1] focus:ring-[#56E8E1]"
                  />
                  <span>Randomize question sequence for candidates</span>
                </label>

                <label className="flex items-center gap-2 text-xs font-semibold text-[#111111] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.random_options}
                    onChange={(e) =>
                      setFormData({ ...formData, random_options: e.target.checked })
                    }
                    className="rounded accent-[#56E8E1] focus:ring-[#56E8E1]"
                  />
                  <span>Shuffle options (A, B, C, D) order</span>
                </label>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#E5E9E9]">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-xs font-semibold text-[#5F6368] hover:bg-[#F0F4F4] rounded-xl transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-[#56E8E1] hover:bg-[#34d5cd] text-[#111111] text-xs font-bold rounded-xl transition-all shadow-xs cursor-pointer"
                >
                  {editingSet ? 'Save Changes' : 'Create Question Set'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Guided 3-Step CSV Import Modal */}
      {isImportModalOpen && (
        <div className="fixed inset-0 z-50 bg-[#111111]/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-2xl w-full p-6 sm:p-7 shadow-2xl border border-[#E5E9E9] max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between pb-3 border-b border-[#E5E9E9] mb-4 shrink-0">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#1b8e88]" />
                <h3 className="text-base font-bold text-[#111111]">
                  Bulk CSV Question Import
                </h3>
              </div>
              <button
                onClick={() => setIsImportModalOpen(false)}
                className="text-[#5F6368] hover:text-[#111111] p-1 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Stepper Wizard Indicator */}
            <div className="flex items-center justify-between px-4 py-2 bg-[#F7FAFA] rounded-2xl mb-4 shrink-0 text-xs font-bold border border-[#E5E9E9]">
              <span className={importStep === 1 ? 'text-[#1b8e88]' : 'text-[#5F6368]'}>
                1. Input CSV Content
              </span>
              <span className="text-[#5F6368]/40">→</span>
              <span className={importStep === 2 ? 'text-[#1b8e88]' : 'text-[#5F6368]'}>
                2. Preview & Validation ({parsedRows.length})
              </span>
            </div>

            {importStep === 1 ? (
              <div className="space-y-4 overflow-y-auto flex-1 pr-1">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 bg-[#E8FAF9] rounded-2xl border border-[#56E8E1]/40 text-xs">
                  <div>
                    <div className="font-bold text-[#111111]">Required CSV Format</div>
                    <div className="text-[11px] text-[#5F6368]">
                      Columns must start with <code className="bg-white px-1 py-0.5 rounded font-mono text-[#1b8e88] font-bold">question_set_id</code>. Do not use an <code className="bg-white px-1 py-0.5 rounded font-mono text-[#D94A5C]">index</code> column.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleDownloadTemplate}
                    className="px-3 py-1.5 rounded-xl bg-white hover:bg-[#F0F4F4] text-[#1b8e88] font-bold border border-[#56E8E1]/60 shrink-0 flex items-center gap-1 cursor-pointer shadow-2xs"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Download CSV Template
                  </button>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#111111] mb-1">
                    Assign Questions to Question Set
                  </label>
                  <select
                    value={importTargetSetId}
                    onChange={(e) => setImportTargetSetId(Number(e.target.value))}
                    className="w-full px-3 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1] bg-white"
                  >
                    {questionSets.map((qs) => (
                      <option key={qs.id} value={qs.id}>
                        Set #{qs.id}: {qs.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-[#5F6368] mt-1">
                    All questions in the uploaded CSV will be imported into this selected destination (<span className="font-semibold text-[#111111]">Set #{importTargetSetId}</span>). The selected destination determines the target Question Set for the entire import.
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#111111] mb-1">
                    Upload CSV File
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      id="csv-file-input"
                      type="file"
                      accept=".csv"
                      onChange={handleFileUpload}
                      className="block w-full text-xs text-[#5F6368] file:mr-3 file:py-2 file:px-3 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-[#E8FAF9] file:text-[#1b8e88] hover:file:bg-[#d5f5f3] cursor-pointer p-1.5 border border-[#E5E9E9] rounded-xl bg-white focus:outline-none"
                    />
                  </div>
                  <p className="text-[11px] text-[#5F6368] mt-1">
                    Select a .csv file from your computer to load questions into the importer.
                  </p>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-bold text-[#111111]">
                      CSV Data Lines
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setImportCsvText(
                          `question_set_id,question_text,option_a,option_b,option_c,option_d,correct_option,difficulty,explanation\n${importTargetSetId},"Which organ pumps blood throughout the human body?","Lungs","Heart","Brain","Liver","B","Easy","The heart is the primary circulatory pump."\n${importTargetSetId},"What is the speed of light in vacuum approximately?","300,000 km/s","150,000 km/s","1,000 km/s","Infinite","A","Medium","The speed of light in vacuum is approx 299,792 km/s."`
                        );
                      }}
                      className="text-xs font-bold text-[#1b8e88] hover:text-[#146e69] cursor-pointer"
                    >
                      Fill Sample Data
                    </button>
                  </div>
                  <textarea
                    rows={8}
                    value={importCsvText}
                    onChange={(e) => setImportCsvText(e.target.value)}
                    placeholder={`question_set_id,question_text,option_a,option_b,option_c,option_d,correct_option,difficulty,explanation\n1,"What is 2+2?","3","4","5","6","B","Easy","Basic addition: 2+2=4"`}
                    className="w-full p-3 font-mono text-xs rounded-2xl border border-[#E5E9E9] focus:outline-none focus:border-[#56E8E1] bg-[#F7FAFA]"
                  />
                </div>

                <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#E5E9E9] shrink-0">
                  <button
                    type="button"
                    onClick={() => setIsImportModalOpen(false)}
                    className="px-4 py-2 text-xs font-semibold text-[#5F6368] hover:bg-[#F0F4F4] rounded-xl transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleParseAndValidateCSV}
                    className="px-5 py-2.5 bg-[#56E8E1] hover:bg-[#34d5cd] text-[#111111] text-xs font-bold rounded-xl transition-all shadow-xs flex items-center gap-1.5 cursor-pointer"
                  >
                    Validate & Preview <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4 overflow-y-auto flex-1 pr-1 flex flex-col">
                {(() => {
                  const newCount = parsedRows.filter((r) => r.valid && !r.isDuplicate).length;
                  const dupCount = parsedRows.filter((r) => r.valid && r.isDuplicate).length;
                  const errCount = parsedRows.filter((r) => !r.valid).length;
                  const readyToImportCount = replaceExistingOnImport ? parsedRows.filter((r) => r.valid).length : newCount;

                  return (
                    <>
                      {/* Selected Target Set Banner */}
                      {detectedSet && (
                        <div className="p-3 bg-[#E8FAF9] rounded-2xl border border-[#56E8E1]/50 flex items-center justify-between text-xs">
                          <div>
                            <div className="font-extrabold text-[#111111] flex items-center gap-1.5">
                              <span className="w-2 h-2 rounded-full bg-[#1b8e88] inline-block" />
                              Target Question Set (Destination: Set #{detectedSet.id})
                            </div>
                            <div className="text-[#5F6368] text-[11px] mt-0.5">
                              {detectedSet.code ? (
                                <span className="font-semibold text-[#1b8e88] mr-2">Code: {detectedSet.code}</span>
                              ) : null}
                              <span>Database ID: #{detectedSet.id}</span> • <span>"{detectedSet.name}"</span>
                            </div>
                          </div>
                          <span className="px-2.5 py-1 rounded-lg bg-white border border-[#56E8E1]/40 text-[#1b8e88] text-[10px] font-bold">
                            Selected Target
                          </span>
                        </div>
                      )}

                      <div className="flex items-center justify-between text-xs font-bold">
                        <span className="text-[#111111]">
                          Validation Results: <span className="text-[#20A66A]">{newCount} New</span> /{' '}
                          <span className="text-[#D97706]">{dupCount} Duplicates</span> /{' '}
                          <span className="text-[#D94A5C]">{errCount} Errors</span>
                        </span>
                        <button
                          onClick={() => setImportStep(1)}
                          className="text-[#1b8e88] hover:underline cursor-pointer"
                        >
                          ← Edit CSV text
                        </button>
                      </div>

                      <div className="space-y-2 max-h-60 overflow-y-auto border border-[#E5E9E9] rounded-2xl p-2 bg-[#F7FAFA]">
                        {parsedRows.map((r, i) => {
                          const isDup = r.valid && r.isDuplicate;
                          const isSkipped = isDup && !replaceExistingOnImport;

                          return (
                            <div
                              key={i}
                              className={`p-3 rounded-xl border text-xs flex items-start gap-2.5 ${
                                !r.valid
                                  ? 'bg-[#FDEDEF] border-[#D94A5C]/30 text-[#D94A5C]'
                                  : isSkipped
                                  ? 'bg-[#FFF7D9] border-[#EBC138]/60 text-[#111111]'
                                  : isDup
                                  ? 'bg-[#E8FAF9] border-[#56E8E1]/40 text-[#111111]'
                                  : 'bg-white border-[#20A66A]/30 text-[#111111]'
                              }`}
                            >
                              <div className="shrink-0 mt-0.5">
                                {!r.valid ? (
                                  <AlertCircle className="w-4 h-4 text-[#D94A5C]" />
                                ) : isDup ? (
                                  <RotateCcw className="w-4 h-4 text-[#8a6508]" />
                                ) : (
                                  <CheckCircle2 className="w-4 h-4 text-[#20A66A]" />
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="font-bold truncate">
                                    {r.data ? r.data.question_text : r.rawText}
                                  </div>
                                  {isDup && (
                                    <span
                                      className={`px-2 py-0.5 rounded-md text-[10px] font-bold shrink-0 ${
                                        replaceExistingOnImport
                                          ? 'bg-[#E8FAF9] text-[#1b8e88] border border-[#56E8E1]/40'
                                          : 'bg-[#FFF0B3] text-[#8a6508] border border-[#EBC138]/60'
                                      }`}
                                    >
                                      {replaceExistingOnImport ? 'Will Overwrite' : 'Duplicate — Skipped'}
                                    </span>
                                  )}
                                </div>
                                {r.error && <div className="text-[11px] text-[#D94A5C] mt-0.5">{r.error}</div>}
                                {r.isDuplicate && (
                                  <div className="text-[11px] text-[#8a6508] font-medium mt-0.5">
                                    {r.duplicateReason}
                                  </div>
                                )}
                                {r.data && (
                                  <div className="text-[11px] text-[#5F6368] mt-0.5">
                                    Correct: Option {r.data.correct_option} • Difficulty: {r.data.difficulty}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div className="pt-2">
                        <label className="flex items-start gap-2.5 text-xs font-semibold text-[#111111] cursor-pointer bg-[#FFF7D9] p-3 rounded-2xl border border-[#EBC138]/40">
                          <input
                            type="checkbox"
                            checked={replaceExistingOnImport}
                            onChange={(e) => setReplaceExistingOnImport(e.target.checked)}
                            className="mt-0.5 rounded accent-[#8a6508] focus:ring-[#8a6508] cursor-pointer"
                          />
                          <div>
                            <span className="text-[#8a6508] font-bold block">
                              Replace existing questions in target Question Set before importing
                            </span>
                            <p className="text-[11px] text-[#5F6368] font-normal mt-0.5 leading-relaxed">
                              If checked, all existing questions in target Question Set #{importTargetSetId} ({questionSets.find((s) => s.id === importTargetSetId)?.name || 'Set'}) will be deleted inside a single transaction before inserting the new CSV questions.
                            </p>
                          </div>
                        </label>
                      </div>

                      <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#E5E9E9] mt-auto shrink-0">
                        <button
                          type="button"
                          onClick={() => setImportStep(1)}
                          className="px-4 py-2 text-xs font-semibold text-[#5F6368] hover:bg-[#F0F4F4] rounded-xl transition-colors cursor-pointer"
                        >
                          Back
                        </button>
                        <button
                          type="button"
                          onClick={handleConfirmImport}
                          disabled={readyToImportCount === 0}
                          className="px-5 py-2.5 bg-[#20A66A] hover:bg-[#188c58] text-white text-xs font-bold rounded-xl transition-all shadow-xs disabled:opacity-40 disabled:pointer-events-none flex items-center gap-1.5 cursor-pointer"
                        >
                          Confirm Import ({readyToImportCount} Questions)
                        </button>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={isReplaceConfirmModalOpen}
        title="Confirm Replace Existing Questions"
        message={`Are you sure you want to REPLACE all existing questions in Question Set #${importTargetSetId} (${questionSets.find((s) => s.id === importTargetSetId)?.name || 'Set'})? This action will permanently delete existing questions in this set and replace them with the CSV rows.`}
        confirmText="Yes, Replace All"
        variant="danger"
        onConfirm={() => {
          setIsReplaceConfirmModalOpen(false);
          executeImport();
        }}
        onCancel={() => setIsReplaceConfirmModalOpen(false)}
      />

      <ConfirmModal
        isOpen={!!deleteSetTarget}
        title="Delete Question Set"
        message={
          deleteSetTarget
            ? `Delete "${deleteSetTarget.name}"? Questions inside will remain in the bank.`
            : ''
        }
        confirmText="Delete Question Set"
        variant="danger"
        onConfirm={confirmDeleteSet}
        onCancel={() => setDeleteSetTarget(null)}
      />

      <ConfirmModal
        isOpen={!!clearSetTarget}
        title="Clear Questions"
        message={
          clearSetTarget
            ? `This will permanently remove all ${
                questions.filter((q) => q.question_set_id === clearSetTarget.id).length
              } questions from ${clearSetTarget.name}. The Question Set itself will NOT be deleted.`
            : ''
        }
        confirmText="Clear Questions"
        variant="danger"
        onConfirm={confirmClearSet}
        onCancel={() => setClearSetTarget(null)}
      />
    </div>
  );
};
